import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FilmIcon, PlayIcon } from "lucide-react";
import type { CreativeLink } from "@scandi/shared";
import { fallBackToOrigin, usableCdn } from "@/lib/preview";
import { cn } from "@/lib/utils";

/**
 * Preview of a creative.
 *
 * Creatives are vertical video, so the frame is 9:16 — a wide crop would show
 * the middle of someone's face and nothing else useful.
 *
 * Degrades in three steps, because every one of them is a normal state: no
 * tracker row, a tracker row with no Drive file, or a file Drive has not
 * generated a thumbnail for yet. None of them should leave a hole in the layout.
 */
export function CreativeThumb({
  link,
  width,
  to,
  noLink = false,
  sizes,
  eager = false,
  className,
}: {
  link: CreativeLink | null;
  /** Rendered width in pixels; the API only serves the sizes listed here. */
  width: 160 | 320;
  /**
   * In-app destination, normally the creative's own page. Takes precedence over
   * Drive: seeing what a creative did is the more common intent, and the file
   * itself is one click further in.
   */
  to?: string;
  /**
   * Render the frame alone, for thumbnails sitting inside something that is
   * already a link. Anchors cannot nest, and a browser will silently break the
   * outer one rather than tell you.
   */
  noLink?: boolean;
  /**
   * The rendered width for `srcset`, when the frame is drawn wider than
   * `width` at some breakpoint (a poster on a grid): the browser then picks
   * the Cloudflare copy that fits. Defaults to `width`.
   */
  sizes?: string;
  /** Loaded first rather than lazily: the pictures above the fold of a ranking. */
  eager?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(link?.thumbnailUrl) && !failed;
  // Straight from Cloudflare when the answer carried signed copies; our own
  // route otherwise, which redirects there itself once the copy exists.
  const cdn = usableCdn(link?.thumbnailCdn);
  const interactive = !noLink && (Boolean(to) || Boolean(link?.driveUrl));

  const frame = (
    <span
      className={cn(
        "bg-muted ring-border/60 relative block shrink-0 overflow-hidden rounded ring-1",
        "aspect-[9/16]",
        className,
      )}
    >
      {showImage ? (
        <>
          <img
            src={cdn ? cdn.src : `${link!.thumbnailUrl!}?w=${width}`}
            srcSet={cdn?.srcSet}
            sizes={cdn ? (sizes ?? `${width}px`) : undefined}
            alt=""
            width={width}
            height={Math.round((width * 16) / 9)}
            loading={eager ? "eager" : "lazy"}
            fetchPriority={eager ? "high" : undefined}
            decoding="async"
            className="size-full object-cover"
            onError={(event) => {
              // A blocked or expired Cloudflare copy: retry once from our route.
              if (cdn && !event.currentTarget.dataset.cdnFallback) {
                event.currentTarget.dataset.cdnFallback = "1";
                event.currentTarget.removeAttribute("srcset");
                event.currentTarget.src = `${link!.thumbnailUrl!}?w=${width}`;
                return;
              }
              if (!fallBackToOrigin(event)) setFailed(true);
            }}
          />
          {interactive && (
            <span className="absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-[opacity,background-color] group-hover/thumb:bg-black/35 group-hover/thumb:opacity-100">
              <PlayIcon className="size-3.5 fill-white text-white" />
            </span>
          )}
        </>
      ) : (
        <span className="flex size-full items-center justify-center">
          <FilmIcon className="text-muted-foreground/40 size-3.5" />
        </span>
      )}
    </span>
  );

  // Stops the click reaching an enclosing collapsible trigger or card link.
  const stop = (e: React.MouseEvent) => e.stopPropagation();
  const wrapper = "group/thumb focus-visible:ring-ring/50 shrink-0 rounded outline-none focus-visible:ring-2";

  if (noLink) return frame;

  if (to) {
    return (
      <Link to={to} onClick={stop} title={t("creative.openCreative")} className={wrapper}>
        {frame}
      </Link>
    );
  }

  if (!link?.driveUrl) return frame;

  return (
    <a
      href={link.driveUrl}
      target="_blank"
      rel="noreferrer"
      onClick={stop}
      title={t("creative.openInDrive", { name: link.fileName })}
      className={wrapper}
    >
      {frame}
    </a>
  );
}
